import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ShoppingCart } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import UserMenu from "./UserMenu";

const CATEGORIES = [
  { key: "nature", label: "Nature" },
  { key: "architecture", label: "Architecture" },
  { key: "people", label: "People" },
  { key: "abstract", label: "Abstract" },
  { key: "travel", label: "Travel" },
  { key: "food", label: "Food" },
  { key: "business", label: "Business" },
  { key: "animals", label: "Animals" },
];

// `compact` and `defaultQuery` are accepted for backwards compatibility with existing callers
// but no longer used — the inline header search bar has been removed per user request.
export default function Header() {
  const [exploreOpen, setExploreOpen] = useState(false);
  const { isAuthed, openAuth } = useAuth();
  const { count: cartCount } = useCart();
  const exploreRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (exploreRef.current && !exploreRef.current.contains(e.target)) setExploreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 bg-ink text-ivory border-b border-ivory/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center gap-6">
        <Link
          to="/dashboard"
          data-testid="brand-link"
          className="font-serif text-2xl tracking-tight shrink-0 text-ivory"
        >
          Elite<span className="text-gold">Stock</span>
        </Link>

        <nav className="ml-auto flex items-center gap-5 text-sm text-ivory/85">
          <div className="relative" ref={exploreRef}>
            <button
              type="button"
              onClick={() => setExploreOpen((v) => !v)}
              data-testid="nav-explore"
              className="inline-flex items-center gap-1 hover:text-gold transition-colors"
            >
              Explore <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {exploreOpen && (
              <div
                className="absolute right-0 mt-3 w-64 bg-white border border-border shadow-2xl z-50 p-2"
                data-testid="explore-dropdown"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground px-3 pt-2 pb-1">
                  Categories
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.key}
                      to={`/dashboard/search?q=${c.key}`}
                      onClick={() => setExploreOpen(false)}
                      data-testid={`explore-cat-${c.key}`}
                      className="px-3 py-2 text-sm text-ink hover:bg-secondary hover:text-gold rounded-sm"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-border mt-2 pt-2 px-3 pb-1">
                  <Link
                    to="/dashboard/search?q=curated"
                    onClick={() => setExploreOpen(false)}
                    className="text-sm text-ink es-gold-underline hover:text-gold"
                  >
                    Editors&apos; picks →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {isAuthed && (
            <Link
              to="/dashboard/library"
              data-testid="nav-library"
              className="hidden sm:inline hover:text-gold transition-colors"
            >
              Library
            </Link>
          )}
          <Link
            to="/dashboard/cart"
            data-testid="nav-cart"
            className="relative inline-flex items-center gap-1 hover:text-gold transition-colors"
            aria-label="Cart"
          >
            <ShoppingCart className="h-4 w-4" />
            {cartCount > 0 && (
              <span data-testid="cart-badge" className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-gold text-ivory text-[11px] inline-flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>
          {isAuthed ? (
            <UserMenu solid={true} />
          ) : (
            <button
              onClick={() => openAuth("login")}
              data-testid="open-auth-modal-btn"
              className="text-xs px-3 py-1.5 border transition-colors border-ivory/60 text-ivory hover:bg-gold hover:border-gold hover:text-ivory"
            >
              Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
